/**
 * Task 20 (README §8; v2.3 §10-11): the read-only closed-thread transcript,
 * and the landing rule for a Halo ticket's deep link back to Communicator —
 * "a Halo deep link resolves to this page when the thread is closed and to
 * the live conversation pane when it is open" (README §8). Both destinations
 * are this same route (`/transcript/:id`, the URL a ticket's link actually
 * points at); this page decides which one to show rather than the link
 * itself branching, since only the thread's *current* state (which can
 * change after the link was written into the ticket) can answer that.
 */
import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import type { ThreadDetail } from '@communicator/shared'
import { ops } from '../lib/ops'
import { useStore } from '../store'
import { ReadOnlyTranscript, isThreadClosed } from '../components/transcript'

export function TranscriptPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const selectThread = useStore((s) => s.selectThread)
  const cfg = useStore((s) => s.cfg)

  const [thread, setThread] = useState<ThreadDetail | null>(null)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    if (!id) return
    setThread(null)
    setFailed(false)
    let cancelled = false

    function landOnLivePane(threadId: string) {
      // The live pane (pane B) reads `activeId` off the store; the route for
      // it is just `/`, same as clicking a row in the queue rail.
      selectThread(threadId)
      navigate('/', { replace: true })
    }

    // A thread the console already has live (kept fresh by the SSE stream,
    // see src/lib/stream.ts) tells us its current state without a network
    // round trip — the same "prefer the live summary" pattern ThreadPane
    // uses (task-15-report.md). This also means an open thread's deep link
    // resolves instantly, with no loading flash before the redirect.
    const known = useStore.getState().threads.find((t) => t.id === id)
    if (known && !isThreadClosed(known.state)) {
      landOnLivePane(known.id)
      return
    }

    // Either unknown to the store's current scope, or known and closed —
    // either way the full record (messages, close note) only exists behind
    // the one-off detail fetch, since ThreadSummary carries neither.
    ops
      .thread(id)
      .then((detail) => {
        if (cancelled) return
        if (!isThreadClosed(detail.state)) {
          landOnLivePane(detail.id)
          return
        }
        setThread(detail)
      })
      .catch(() => {
        if (!cancelled) setFailed(true)
      })

    return () => {
      cancelled = true
    }
  }, [id, navigate, selectThread])

  if (!id) {
    return <p className="p-4 text-[13px] text-mid">No thread id given.</p>
  }

  if (failed) {
    return (
      <p className="p-4 text-[13px] text-mid" data-testid="transcript-error">
        Couldn&apos;t load this transcript. It may not exist.
      </p>
    )
  }

  if (!thread) {
    return (
      <p className="p-4 text-[13px] text-mid" data-testid="transcript-loading">
        Loading transcript {id}…
      </p>
    )
  }

  return <ReadOnlyTranscript thread={thread} cfg={cfg} />
}
